import { Injectable } from "@angular/core";
import { User } from "../models/auth.models";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { catchError, map, switchMap, tap } from "rxjs/operators";
import { BehaviorSubject, Observable, of, Subject, throwError } from "rxjs";
import { Store } from "@ngrx/store";
import { loginFailure, logout } from "src/app/store/Authentication/authentication.actions";
import { environment } from "src/environments/environment";
import { NGXLogger } from "ngx-logger";
import { CURRENT_USER, TOKEN } from "../const/constant";
import { LocalStorageService } from "./local-storage.service";
import { RestApiService } from "./rest-api.service";
import { PermissionEnum } from "../enum/permission";
import { Router } from "@angular/router";
import { Toast } from "ngx-toastr";
import { ToastService } from "./toast.service";

const AUTH_API = environment.apiURL;

const httpOptions = {
	headers: new HttpHeaders({ "Content-Type": "application/json" }),
};

@Injectable({ providedIn: "root" })

/**
 * Auth-service Component
 */
export class AuthenticationService {
	currentUserValue: User;

	public currentUserSubject: BehaviorSubject<User | null>;
	public currentUser$: Observable<User | null>;

	constructor(
		private http: HttpClient,
		private store: Store,
		private logger: NGXLogger,
		private localStorageService: LocalStorageService,
		private restApiService: RestApiService,
		private toastService: ToastService,
		private router: Router,
	) {
		this.currentUserSubject = new BehaviorSubject<User>(JSON.parse(sessionStorage.getItem("currentUser")!));
		this.currentUser$ = this.currentUserSubject.asObservable();
		const rest = sessionStorage.getItem("isLoggedIn") as unknown as boolean;
	}

	register({ email, username, nom, password, phone }) {
		// return getFirebaseBackend()!.registerUser(email, password).then((response: any) => {
		//     const user = response;
		//     return user;
		// });

		// Register Api
		return this.http
			.post(
				environment.apiURL + "/auth/local/register",
				{
					email,
					username,
					password,
					phone,
					nom,
					role: "collaborateur",
					confirmed: false,
					blocked: true,
				},
				httpOptions,
			)
			.pipe(
				map((response: any) => {
					this.currentUserSubject.next(response.user);
					return this.currentUser$;
				}),
				catchError((error: any) => {
					const errorMessage = "Login failed";
					let msg = "Erreur lors de l'inscription.";

					if (error?.error?.error?.message) {
						msg = error.error.error.message;
					} else if (error?.error?.message) {
						msg = error.error.message;
					} else if (typeof error?.error === "string") {
						msg = error.error;
					}

					// 2. Traduction amicale pour l'utilisateur (Optionnel mais recommandé)
					if (msg === "Email or Username are already taken") {
						msg = "Cet email est déjà utilisé par un autre compte.";
					}

					this.toastService.error(msg);
					this.store.dispatch(loginFailure({ error: errorMessage }));
					return throwError(errorMessage);
				}),
			);
	}

	/*login(email: string, password: string): Observable<any> {
		return this.http
			.post(
				AUTH_API + "/auth/login",
				{
					data: {
						email, // Directly send email and password in the request body
						password,
					},
				},
				httpOptions
			)
			.pipe(
				map((response: any) => {
					// Update the current user and store data in localStorage
					this.currentUserSubject.next(response.user);
					this.localStorageService.set(CURRENT_USER, JSON.stringify(response.user));
					this.localStorageService.set(TOKEN, response.jwt);
					return response; // Return the response for further processing
				}),
				catchError((error) => {
					// Handle errors
					this.currentUserSubject.next(null);
					this.localStorageService.set("isLoggedIn", "false");
					console.error("Login failed:", error);
					return throwError(() => new Error("Login failed")); // Propagate the error
				})
			);
	}*/
	login(email: string, password: string): Observable<any> {
		return this.http
			.post<User>(AUTH_API + "/auth/login", {
				data: {
					email, // Directly send email and password in the request body
					password,
				},
			})
			.pipe(
				tap((user: any) => {
					this.currentUserSubject.next(user.user);
					this.localStorageService.set(CURRENT_USER, JSON.stringify(user.user));
					this.localStorageService.set(TOKEN, user.jwt);
				}),
			);
	}

	getUserDetail(id: number) {
		return this.restApiService.getCustom(`users/${id}?populate[role]=true&populate[extraRoles]=true&populate[avatar]=true`).pipe(
			map((user: any) => {
				if (user.avatar) {
					this.localStorageService.set("avatar", user.avatar.url);
				}
				this.currentUserValue = user;
				this.localStorageService.set(CURRENT_USER, JSON.stringify(user));
			}),
			catchError((error) => {
				return throwError(error);
			}),
		);
	}

	public currentUser(): User {
		return this.currentUserValue;
	}
	logout() {
		this.store.dispatch(logout());
		this.localStorageService.remove("currentUser");
		this.localStorageService.remove("token");
		this.localStorageService.remove("isLoggedIn");
		this.localStorageService.remove("avatar");
		this.currentUserSubject.next(null!);
		this.currentUserValue = null;
		return of(undefined).pipe();
	}

	isDemandeur(): boolean {
		return this.hasPermission([PermissionEnum.DEMANDEUR]);
	}

	isSuperviseur() {
		return this.hasPermission([PermissionEnum.SUPERVISEUR]);
	}

	isMagasinerCentral() {
		// Le master data a les mêmes droits que le magasinier central
		return this.hasPermission([PermissionEnum.MAGASINIER, PermissionEnum.MASTER_DATA]);
	}
	isMasterData() {
		return this.hasPermission([PermissionEnum.MASTER_DATA]);
	}
	isMagasinerInformatique() {
		return this.hasPermission([PermissionEnum.MAGASINIER_INFORMATIQUE]);
	}

	isAdmin() {
		return this.hasPermission([PermissionEnum.ADMIN]);
	}
	isControleGestion() {
		return this.hasPermission([PermissionEnum.CONTROLE_GESTION]);
	}

	isAchat() {
		return this.hasPermission([PermissionEnum.ACHETEUR]);
	}

	isSuperviseurAchat() {
		return this.hasPermission([PermissionEnum.SUPERVISEUR_ACHAT]);
	}

	isChefChantier() {
		return this.hasPermission([PermissionEnum.CHEF_CHANTIER]);
	}

	isGestionnaireBC() {
		return this.hasPermission([PermissionEnum.GESTIONNAIREBC]);
	}

	isChiffrage() {
		return this.hasPermission([PermissionEnum.CHIFFRAGE]);
	}

	isTresorier() {
		return this.hasPermission([PermissionEnum.TRESORERIE]);
	}

	isTresorierBasic() {
		return this.hasPermission([PermissionEnum.TRESORERIE_BASIC]);
	}

	isResponsableProjet() {
		return this.hasPermission([PermissionEnum.RESPONSABLE_PROJET]);
	}

	/** Rôles qui ouvrent le module commercial en entier (les droits des rôles s'additionnent). */
	static readonly ROLES_COMMERCIAL_COMPLET = [
		PermissionEnum.ADMIN,
		PermissionEnum.CONTROLE_GESTION,
		PermissionEnum.GESTIONNAIRE_COMMERCIAL,
		PermissionEnum.FINANCE,
		PermissionEnum.DIRECTION,
	];

	/**
	 * Vrai si l'utilisateur n'accède au module commercial qu'en tant que responsable de projet :
	 * il consulte les décomptes de ses projets et crée, publie et consulte les devis de ses projets.
	 */
	estResponsableProjetSeulement(): boolean {
		return this.isResponsableProjet() && !this.hasPermission(AuthenticationService.ROLES_COMMERCIAL_COMPLET);
	}

	/**
	 * Vrai si l'utilisateur n'accède aux devis qu'en tant que superviseur (rôle « superviseur » des demandes) :
	 * il consulte et valide les devis dont il est le superviseur, sans en créer.
	 */
	estSuperviseurDevisSeulement(): boolean {
		return this.isSuperviseur() && !this.isResponsableProjet() && !this.hasPermission(AuthenticationService.ROLES_COMMERCIAL_COMPLET);
	}

	resetPassword(email: string) {}

	/**
	 * Lower-cased role types held by a user object, whatever its shape:
	 * - login payload: { roleTypes: string[] } (or legacy { role: "admin" })
	 * - /users/:id payload: { role: { type }, extraRoles: [{ type }] }
	 */
	roleTypesOf(user: any): string[] {
		if (!user) return [];
		let types: string[] = [];
		if (Array.isArray(user.roleTypes)) {
			types = user.roleTypes;
		} else if (Array.isArray(user.roles)) {
			types = user.roles.map((r: any) => r?.type);
		} else {
			const primary = typeof user.role === "string" ? user.role : user.role?.type;
			const extras = Array.isArray(user.extraRoles) ? user.extraRoles.map((r: any) => r?.type) : [];
			types = [primary, ...extras];
		}
		return types.filter(Boolean).map((t: string) => String(t).toLowerCase());
	}

	/** Role types of the stored current user (primary first). */
	getRoleTypes(): string[] {
		const user: any = JSON.parse(this.localStorageService.get(CURRENT_USER) || "{}");
		return this.roleTypesOf(user);
	}

	/** True if the current user holds ANY of the given role types. */
	hasRole(...types: string[]): boolean {
		const held = this.getRoleTypes();
		return types.flat().some((t) => held.includes(String(t).toLowerCase()));
	}

	hasPermission(permissions: string[]): boolean {
		return this.hasRole(...permissions);
	}

	/** Landing page per role type; the first held role with an entry wins (primary first). */
	private static readonly ROLE_HOME: Record<string, string[]> = {
		[PermissionEnum.ADMIN]: ["/"],
		[PermissionEnum.DEMANDEUR]: ["/demandes"],
		[PermissionEnum.SUPERVISEUR]: ["/demandes"],
		[PermissionEnum.CHEF_CHANTIER]: ["demandes"],
		[PermissionEnum.MAGASINIER]: ["/magasin"],
		[PermissionEnum.MASTER_DATA]: ["/magasin"],
		[PermissionEnum.MAGASINIER_INFORMATIQUE]: ["/magasin"],
		[PermissionEnum.ACHETEUR]: ["/achat"],
		[PermissionEnum.GESTIONNAIREBC]: ["/achat/bons-commandes"],
		[PermissionEnum.SUPERVISEUR_ACHAT]: ["/achat"],
		[PermissionEnum.GESTIONNAIRE_COMMERCIAL]: ["administration/projets"],
		[PermissionEnum.BET]: ["timesheet/taches"],
		[PermissionEnum.SUPERVISEUR_BET]: ["timesheet/taches"],
		[PermissionEnum.RH]: ["rh/employes"],
		[PermissionEnum.CHIFFRAGE]: ["/gestion/base-articles"],
		[PermissionEnum.TRESORERIE]: ["/finance/demandes-paiement"],
		[PermissionEnum.TRESORERIE_BASIC]: ["/finance/demandes-paiement"],
		[PermissionEnum.COLLABORATEUR]: ["/rh/demandes"],
		[PermissionEnum.CONTROLE_GESTION]: ["/rh/demandes"],
		[PermissionEnum.COORDINATION_RH]: ["/rh/demandes"],
		[PermissionEnum.DIRECTION]: ["/dashboard/general"],
		[PermissionEnum.FINANCE]: ["gestion-commercial/factures"],
		[PermissionEnum.RESPONSABLE_PROJET]: ["gestion-commercial/devis"],
	};

	/** Accepts a single role type or the roleTypes[] list (primary first). */
	redirectBasedOnRole(roles: string | string[] | undefined) {
		const held = (Array.isArray(roles) ? roles : [roles]).filter(Boolean).map((r) => String(r).toLowerCase());
		const homes = AuthenticationService.ROLE_HOME;
		const lowerHomes: Record<string, string[]> = {};
		for (const k of Object.keys(homes)) lowerHomes[k.toLowerCase()] = homes[k];
		const match = held.find((r) => lowerHomes[r]);
		this.router.navigate(match ? lowerHomes[match] : ["/"]);
	}
}
